import { navigate } from 'gatsby'
import { useStore } from './useStore'

const fetch_retry = async (url, options, n) => {
  await sleep(100)
  try {
    return await fetch(url, options).then(res => {
      if (!res.ok) return Promise.reject(res)
      return res.json()
    })
  } catch (err) {
    if (n === 1) throw err
    return await fetch_retry(url, options, n - 1)
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function base64toBlob(base64Data, contentType) {
  contentType = contentType || ''
  var sliceSize = 1024
  var byteCharacters = atob(base64Data)
  var bytesLength = byteCharacters.length
  var slicesCount = Math.ceil(bytesLength / sliceSize)
  var byteArrays = new Array(slicesCount)

  for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    var begin = sliceIndex * sliceSize
    var end = Math.min(begin + sliceSize, bytesLength)

    var bytes = new Array(end - begin)
    for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0)
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes)
  }
  return new Blob(byteArrays, { type: contentType })
}

/**
 * Inject ReferenceID / MustAttachProofOfInsurance when missing from formik values.
 */
function prepareSubmissionValues(state, values) {
  const referenceId =
    values?.ReferenceID ??
    values?.ReferenceId ??
    values?.refId ??
    state.form?.ReferenceID ??
    state.form?.refId ??
    state.borrower?.ReferenceID ??
    state.borrower?.ReferenceId ??
    ''

  const mustAttachProofOfInsurance =
    values?.MustAttachProofOfInsurance ??
    state.form?.MustAttachProofOfInsurance ??
    state.borrower?.MustAttachProofOfInsurance ??
    false

  return {
    ...values,
    ReferenceID: referenceId,
    MustAttachProofOfInsurance: mustAttachProofOfInsurance,
  }
}

function shouldSkipInsuranceDocument(values) {
  const referenceId = values.ReferenceID ?? values.ReferenceId ?? values.refId
  const mustAttach = values.MustAttachProofOfInsurance
  return !referenceId || mustAttach === 'true' || mustAttach === true
}

/**
 * Upload files to attachment API.
 * Shared by review-page flow and dashboard upload modal.
 * @param {Array<{ name: string, base64: string, type?: string }>} files
 * @param {object} formPayload
 * @returns {Promise<Array<{ attachmentFileName: string, attachmentId: string, originalName: string }>>}
 */
async function uploadInsuranceDocuments(files, formPayload) {
  if (!files?.length) return []

  const uploadFileDataStream = async file => {
    const data = new FormData()
    const tempData = {
      ReferenceId: formPayload.ReferenceID,
      IsLimc: formPayload.MustAttachProofOfInsurance ?? false,
      IsMortgage: formPayload.IsMortgage ?? false,
      IsAgent: formPayload.IsAgent ?? false,
      LenderId: formPayload.LenderID ?? '',
      LenderCode: formPayload.LenderCode ?? '',
      PolicyholderEmailAddress: formPayload.PolicyholderEmailAddress ?? '',
    }
    Object.entries(tempData).forEach(([key, value]) => data.append(key, value))
    const fileBlob = base64toBlob(file.base64, file.type)
    data.append('File', fileBlob, file.name)
    return fetch_retry(
      `${process.env.GATSBY_API_URL}/attachment`,
      { method: 'POST', body: data },
      3
    )
  }

  const args = await Promise.all(
    files.map(async file => {
      const data = await uploadFileDataStream(file)
      return {
        attachmentFileName: data?.DocumentName,
        attachmentId: data?.DocumentId,
        originalName: file.name,
      }
    })
  )

  return args
}

/**
 * Submit insurance document with full form values and optional attachment metadata.
 * @param {object} values Form values
 * @param {Array<{ attachmentFileName: string, attachmentId: string, originalName: string }>|null} args
 */
async function submitInsuranceDocument(values, args) {
  if (shouldSkipInsuranceDocument(values)) {
    return
  }
  return fetch_retry(
    `${process.env.GATSBY_API_URL}/insurance-document`,
    {
      method: 'POST',
      headers: {
        accept: 'text/plain',
        'Content-Type': 'application/json',
        requestSource: 'beta',
      },
      body: JSON.stringify({
        ...values,
        AttachmentFiles: args ? args.map(arg => arg?.attachmentFileName) : [],
        AttachmentIDs: args ? args.map(arg => arg?.attachmentId) : [],
      }),
    },
    3
  )
}

const useFinalSubmission = location => {
  const { state } = useStore()

  const submitFinal = async (files, values) => {
    try {
      const submissionValues = prepareSubmissionValues(state, values)
      const args = await uploadInsuranceDocuments(files, submissionValues)
      await submitInsuranceDocument(submissionValues, args)
      navigate(location.pathname.replace('review', 'confirmation'), {
        state: [
          { Event: 'insurance' },
          ...(args?.length > 0
            ? [{ Event: 'attach', files: args.map(x => x.originalName) }]
            : []),
        ],
      })
    } catch (error) {
      if (error.status === 503) {
        navigate('/systems-down/')
      }

      throw error
    }
  }

  return submitFinal
}

export { useFinalSubmission, uploadInsuranceDocuments, submitInsuranceDocument }
